import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Container, Modal, Form, Tabs, Tab, Row, Col } from 'react-bootstrap';
import axios from 'axios';

const AdminApprovals = () => {
  const [providers, setProviders] = useState([]); // Pending providers
  const [allUsers, setAllUsers] = useState([]); // All users from DB to filter approved providers
  const [appointments, setAppointments] = useState([]); // All reservations for stats
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Active Tab
  const [activeTab, setActiveTab] = useState('approvals');

  // Approval Modal States
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [assignedActivity, setAssignedActivity] = useState('');

  // Commission Modal States
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedProviderForCommission, setSelectedProviderForCommission] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState(10);

  // Resource Config Modal States
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedProviderForResources, setSelectedProviderForResources] = useState(null);
  const [bookingMode, setBookingMode] = useState('standard');
  const [resourceName, setResourceName] = useState('Table');
  const [resourcesList, setResourcesList] = useState([]);
  const [tempResourceName, setTempResourceName] = useState('');
  const [tempResourceCapacity, setTempResourceCapacity] = useState(2);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPendingProviders();
    fetchActivities();
    fetchAllUsers();
    fetchAppointments();
  }, []);

  const fetchPendingProviders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/auth/providers/pending', {
        headers: { Authorization: token }
      });
      setProviders(res.data);
    } catch (err) {
      console.error("Error fetching pending providers:", err);
      setError("Impossible de charger la liste des prestataires en attente.");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await axios.get('/api/activities-hub/activities');
      setActivities(res.data);
    } catch (err) {
      console.error("Error fetching dynamic activities:", err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get('/api/auth/AllUser');
      setAllUsers(res.data.AllUser || []);
    } catch (err) {
      console.error("Error fetching all users:", err);
    }
  };

  const fetchAppointments = async () => {
    try {
      const res = await axios.get('/api/appointments', {
        headers: { Authorization: token }
      });
      setAppointments(res.data);
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
  };

  const handleOpenApproveModal = (provider) => {
    setSelectedProvider(provider);
    
    // Attempt to match provider's self-declared activity
    const matched = activities.find(
      a => a.name.toLowerCase() === provider.activity?.toLowerCase()
    );
    
    if (matched) {
      setAssignedActivity(matched.name);
    } else {
      setAssignedActivity(activities[0]?.name || '');
    }
    
    setShowApproveModal(true);
  };

  const handleAction = async (id, status, finalActivity = '') => {
    setError('');
    setSuccess('');
    try {
      const payload = { status };
      if (status === 'approved' && finalActivity) {
        payload.activity = finalActivity;
      }

      await axios.put(`/api/auth/providers/${id}/approve`, payload, {
        headers: { Authorization: token }
      });
      
      setSuccess(`Le prestataire a été ${status === 'approved' ? 'approuvé' : 'rejeté'} avec succès !`);
      setShowApproveModal(false);
      setSelectedProvider(null);
      
      // Refresh data
      fetchPendingProviders();
      fetchAllUsers();
    } catch (err) {
      console.error("Error updating provider status:", err);
      setError("Erreur lors de la mise à jour du statut du prestataire.");
    }
  };

  // Commission rate updates
  const handleOpenCommissionModal = (provider) => {
    setSelectedProviderForCommission(provider);
    setNewCommissionRate(provider.commissionRate || 10);
    setShowCommissionModal(true);
  };

  const handleSaveCommission = async () => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/auth/providers/${selectedProviderForCommission._id}/commission`, {
        commissionRate: newCommissionRate
      }, {
        headers: { Authorization: token }
      });
      setSuccess(`Commission mise à jour avec succès pour ${selectedProviderForCommission.businessName} !`);
      setShowCommissionModal(false);
      fetchAllUsers();
      fetchAppointments(); // Refresh appointment stats if rates changed
    } catch (err) {
      console.error("Error saving commission rate:", err);
      setError("Erreur lors de la sauvegarde du taux de commission.");
    }
  };

  // Resource config updates
  const handleOpenResourceModal = (provider) => {
    setSelectedProviderForResources(provider);
    setBookingMode(provider.bookingMode || 'standard');
    setResourceName(provider.resourceName || 'Table');
    setResourcesList(provider.resources || []);
    setTempResourceName('');
    setTempResourceCapacity(2);
    setShowResourceModal(true);
  };

  const handleAddResource = () => {
    if (!tempResourceName.trim()) return;
    setResourcesList(prev => [...prev, {
      name: tempResourceName.trim(),
      capacity: parseInt(tempResourceCapacity) || 1
    }]);
    setTempResourceName('');
    setTempResourceCapacity(2);
  };

  const handleRemoveResource = (index) => {
    setResourcesList(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveResourcesConfig = async () => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/auth/providers/${selectedProviderForResources._id}/booking-mode`, {
        bookingMode,
        resourceName,
        resources: resourcesList
      }, {
        headers: { Authorization: token }
      });
      setSuccess(`Configuration de réservation mise à jour pour ${selectedProviderForResources.businessName} !`);
      setShowResourceModal(false);
      fetchAllUsers();
    } catch (err) {
      console.error("Error saving resource configuration:", err);
      setError("Erreur lors de la sauvegarde de la configuration.");
    }
  };

  // Financial calculations
  const nonCancelledBookings = appointments.filter(a => a.status !== 'cancelled');
  const totalVolume = nonCancelledBookings.reduce((acc, a) => acc + (a.totalPrice || 0), 0);
  const totalCommission = nonCancelledBookings.reduce((acc, a) => acc + (a.commissionAmount || 0), 0);

  const getProviderLabel = (provIdOrObj) => {
    if (!provIdOrObj) return "Inconnu";
    if (typeof provIdOrObj === 'object') {
      return provIdOrObj.businessName || provIdOrObj.name || "Inconnu";
    }
    const prov = allUsers.find(u => u._id === provIdOrObj);
    return prov ? (prov.businessName || prov.name) : "Inconnu";
  };

  const getProviderActivity = (prov) => {
    if (prov.activity && typeof prov.activity === 'object') {
      return prov.activity.name;
    }
    return prov.activity || "Non spécifié";
  };

  return (
    <Container style={{ marginTop: '100px', marginBottom: '40px' }}>
      <div className="mb-4">
        <h2 className="text-primary font-weight-bold mb-1">🛡️ Console Super Administration</h2>
        <p className="text-muted">Gérez les validations, les commissions financières et la configuration des ressources par prestataire.</p>
      </div>

      {error && <div className="alert alert-danger rounded-3">{error}</div>}
      {success && <div className="alert alert-success rounded-3">{success}</div>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 nav-tabs-custom">
        {/* TAB 1: approvals */}
        <Tab eventKey="approvals" title="📥 Demandes d'adhésion">
          <Card className="shadow border-0 rounded-4 mt-3">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title as="h4" className="text-secondary font-weight-bold mb-0">Demandes en attente ({providers.length})</Card.Title>
                <Button variant="outline-primary" size="sm" onClick={fetchPendingProviders} disabled={loading}>
                  Actualiser
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-5 text-muted">Chargement en cours...</div>
              ) : providers.length === 0 ? (
                <div className="text-center py-5 bg-light rounded-3 text-muted">
                  Aucune demande d'inscription en attente.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover striped bordered className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Établissement</th>
                        <th>Activité Demandée</th>
                        <th>Email Pro</th>
                        <th>Téléphone</th>
                        <th>Adresse</th>
                        <th className="text-center" style={{ width: '220px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map((p) => (
                        <tr key={p._id}>
                          <td><strong>{p.businessName}</strong></td>
                          <td><Badge bg="secondary" className="px-3 py-2">{getProviderActivity(p)}</Badge></td>
                          <td>{p.email}</td>
                          <td>{p.phone || 'Non renseigné'}</td>
                          <td>{p.adress || 'Non renseignée'}</td>
                          <td className="text-center">
                            <div className="d-flex gap-2 justify-content-center">
                              <Button variant="success" size="sm" onClick={() => handleOpenApproveModal(p)}>
                                Approuver
                              </Button>
                              <Button variant="danger" size="sm" onClick={() => handleAction(p._id, 'rejected')}>
                                Rejeter
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: dashboard / revenue stats */}
        <Tab eventKey="dashboard" title="📊 Tableau de Bord & Revenus">
          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm rounded-4 bg-primary text-white p-4 h-100">
                <Card.Text className="text-uppercase font-weight-bold text-white-50 mb-1">Total Réservations</Card.Text>
                <h2 className="display-4 font-weight-bold mb-0">{nonCancelledBookings.length}</h2>
                <small className="text-white-50">Annulations non comptabilisées</small>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm rounded-4 bg-success text-white p-4 h-100">
                <Card.Text className="text-uppercase font-weight-bold text-white-50 mb-1">Volume d'affaires (Volume)</Card.Text>
                <h2 className="display-4 font-weight-bold mb-0">{totalVolume.toFixed(2)} DT</h2>
                <small className="text-white-50">Chiffre d'affaires brut généré</small>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm rounded-4 bg-warning text-dark p-4 h-100">
                <Card.Text className="text-uppercase font-weight-bold text-dark-50 mb-1">Revenus Commissions</Card.Text>
                <h2 className="display-4 font-weight-bold mb-0">{totalCommission.toFixed(2)} DT</h2>
                <small className="text-dark-50">Commission brute perçue</small>
              </Card>
            </Col>
          </Row>

          <Card className="shadow border-0 rounded-4">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title as="h4" className="text-secondary font-weight-bold mb-0">Historique des Réservations</Card.Title>
                <Button variant="outline-primary" size="sm" onClick={fetchAppointments}>Actualiser</Button>
              </div>

              {appointments.length === 0 ? (
                <div className="text-center py-5 bg-light rounded-3 text-muted">
                  Aucune réservation enregistrée.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover striped bordered className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Client</th>
                        <th>Prestataire</th>
                        <th>Prestation</th>
                        <th>Date & Heure</th>
                        <th>Durée</th>
                        <th>Prix Total</th>
                        <th>Commission (%)</th>
                        <th>Revenu Commission</th>
                        <th>Type / Ressources</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((a) => (
                        <tr key={a._id}>
                          <td>
                            <strong>{a.clientName}</strong><br/>
                            <small className="text-muted">{a.clientPhone || a.clientEmail}</small>
                          </td>
                          <td>{getProviderLabel(a.provider)}</td>
                          <td>
                            {a.services && a.services.length > 0 ? (
                              <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0, fontSize: '12px' }}>
                                {a.services.map((s, idx) => (
                                  <li key={idx}>• {s.name}</li>
                                ))}
                              </ul>
                            ) : (
                              <span>{a.service?.name || a.pack?.name || 'Prestation standard'}</span>
                            )}
                          </td>
                          <td>
                            {new Date(a.date).toLocaleDateString('fr-FR')} à <strong>{a.startTime}</strong>
                          </td>
                          <td>{a.duration} min</td>
                          <td><strong>{a.totalPrice?.toFixed(2)} DT</strong></td>
                          <td>{a.commissionRate}%</td>
                          <td><span className="text-success fw-bold">+{a.commissionAmount?.toFixed(2)} DT</span></td>
                          <td>
                            {a.bookedResource ? (
                              <Badge bg="dark" className="px-2 py-1">
                                🪑 {typeof a.bookedResource === 'object' ? a.bookedResource.name : a.bookedResource} ({a.guestsCount || 1} pers)
                              </Badge>
                            ) : (
                              <span className="text-muted">Standard</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={
                              a.status === 'confirmed' ? 'success' :
                              a.status === 'pending' ? 'warning' :
                              a.status === 'cancelled' ? 'danger' : 'secondary'
                            } className="px-3 py-1">
                              {a.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 3: Provider settings (commissions and resources configuration) */}
        <Tab eventKey="providers" title="⚙️ Gestion des Prestataires">
          <Card className="shadow border-0 rounded-4 mt-3">
            <Card.Body className="p-4">
              <Card.Title as="h4" className="text-secondary font-weight-bold mb-4">Paramètres Financiers & Réservations</Card.Title>
              
              <div className="table-responsive">
                <Table hover striped bordered className="align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Prestataire</th>
                      <th>Activité</th>
                      <th>Commission (%)</th>
                      <th>Mode Réservation</th>
                      <th>Ressources Actives</th>
                      <th className="text-center" style={{ width: '280px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.filter(u => u.role === 'provider' && u.isApproved === 'approved').map((prov) => (
                      <tr key={prov._id}>
                        <td>
                          <strong>{prov.businessName || prov.name}</strong><br/>
                          <small className="text-muted">{prov.email}</small>
                        </td>
                        <td>
                          <Badge bg="info">{getProviderActivity(prov)}</Badge>
                        </td>
                        <td>
                          <span className="fw-bold">{prov.commissionRate || 10}%</span>
                        </td>
                        <td>
                          <Badge bg={prov.bookingMode === 'resources' ? 'dark' : 'secondary'}>
                            {prov.bookingMode === 'resources' ? `Ressources (${prov.resourceName || 'Table'})` : 'Standard (Créneau)'}
                          </Badge>
                        </td>
                        <td>
                          {prov.bookingMode === 'resources' && prov.resources ? (
                            <div className="d-flex flex-wrap gap-1">
                              {prov.resources.map((r, i) => (
                                <Badge key={i} bg="light" className="text-dark border">
                                  {r.name} ({r.capacity} pers)
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex gap-2 justify-content-center">
                            <Button variant="primary" size="sm" onClick={() => handleOpenCommissionModal(prov)}>
                              Commission %
                            </Button>
                            <Button variant="dark" size="sm" onClick={() => handleOpenResourceModal(prov)}>
                              Configuration
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Approval Assignment Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1b2a4c', color: '#fff' }}>
          <Modal.Title className="text-white">✅ Confirmer l'Adhésion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Établissement :</strong> {selectedProvider?.businessName}
          </div>
          <div className="mb-3">
            <strong>Activité demandée :</strong> <Badge bg="secondary">{getProviderActivity(selectedProvider || {})}</Badge>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Affecter à l'activité officielle :</Form.Label>
            <Form.Select
              value={assignedActivity}
              onChange={e => setAssignedActivity(e.target.value)}
              required
            >
              <option value="">-- Choisir une activité officielle --</option>
              {activities.map(act => (
                <option key={act._id} value={act.name}>
                  {act.family?.emoji} {act.family?.name} &gt; {act.emoji} {act.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Cela classera automatiquement ce prestataire dans la bonne famille d'activités sur la page d'accueil.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>Annuler</Button>
          <Button 
            variant="success" 
            onClick={() => handleAction(selectedProvider?._id, 'approved', assignedActivity)}
            disabled={!assignedActivity}
          >
            Confirmer l'Approbation
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Commission Rate Modification Modal */}
      <Modal show={showCommissionModal} onHide={() => setShowCommissionModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1b2a4c', color: '#fff' }}>
          <Modal.Title className="text-white">⚙️ Ajuster la Commission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Établissement :</strong> {selectedProviderForCommission?.businessName}
          </div>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Taux de Commission (%) :</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={newCommissionRate}
              onChange={(e) => setNewCommissionRate(e.target.value)}
            />
            <Form.Text className="text-muted">
              Ce pourcentage sera prélevé sur chaque réservation effectuée chez ce prestataire par des clients.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommissionModal(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleSaveCommission}>Sauvegarder</Button>
        </Modal.Footer>
      </Modal>

      {/* Provider Resources Config Modal */}
      <Modal show={showResourceModal} onHide={() => setShowResourceModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: '#1b2a4c', color: '#fff' }}>
          <Modal.Title className="text-white">🪑 Configuration des Réservations par Ressources</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Établissement :</strong> {selectedProviderForResources?.businessName}
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Mode de réservation :</Form.Label>
            <Form.Select value={bookingMode} onChange={e => setBookingMode(e.target.value)}>
              <option value="standard">Standard (Simple créneau horaire sans ressources spécifiques)</option>
              <option value="resources">Par Ressources (Tables, Terrains de sport, Places, Salles de réunion)</option>
            </Form.Select>
          </Form.Group>

          {bookingMode === 'resources' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Nom générique de la ressource :</Form.Label>
                <Form.Control 
                  type="text" 
                  value={resourceName} 
                  onChange={e => setResourceName(e.target.value)}
                  placeholder="Ex: Table, Terrain, Bureau"
                />
                <Form.Text className="text-muted">
                  Le type d'unité réservée (ex: "Table" pour un restaurant, "Terrain" pour du sport).
                </Form.Text>
              </Form.Group>

              <hr/>

              <div className="mb-3">
                <Form.Label className="fw-bold">Ajouter une ressource :</Form.Label>
                <Row className="align-items-end g-2">
                  <Col md={6}>
                    <Form.Control 
                      type="text" 
                      placeholder="Nom de la ressource (ex: Table 1, Court central)" 
                      value={tempResourceName}
                      onChange={e => setTempResourceName(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control 
                      type="number" 
                      placeholder="Capacité max d'invités" 
                      value={tempResourceCapacity}
                      onChange={e => setTempResourceCapacity(e.target.value)}
                      min="1"
                    />
                  </Col>
                  <Col md={2}>
                    <Button variant="success" className="w-100" onClick={handleAddResource}>Ajouter</Button>
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <Form.Label className="fw-bold">Liste des ressources enregistrées :</Form.Label>
                {resourcesList.length === 0 ? (
                  <div className="text-center py-3 bg-light rounded text-muted">Aucune ressource configurée.</div>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <Table size="sm" bordered hover className="align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Nom</th>
                          <th>Capacité d'invités</th>
                          <th className="text-center" style={{ width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resourcesList.map((res, index) => (
                          <tr key={index}>
                            <td>{res.name}</td>
                            <td>{res.capacity} personnes max</td>
                            <td className="text-center">
                              <Button variant="danger" size="sm" onClick={() => handleRemoveResource(index)}>
                                Retirer
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResourceModal(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleSaveResourcesConfig}>Enregistrer les paramètres</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminApprovals;
